import { useEffect, useCallback, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications, Token, PushNotificationSchema, ActionPerformed } from '@capacitor/push-notifications';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { ensureSession } from '@/lib/ensureSession';

// Key for tracking if permission has been requested
const PERMISSION_PROMPTED_KEY = 'push_permission_prompted';

// Module-level guard so listener registration only runs once per session,
// regardless of how many components mount usePushNotifications().
let globalListenersSetup = false;

export type NotificationTrigger = 
  | 'workout_completed'
  | 'routine_saved'
  | 'streak_started'
  | 'streak_warning'
  | 'coach_followup'
  | 'onboarding_complete'
  | 'manual';

export function usePushNotifications() {
  const { user } = useAuth();
  const isNative = Capacitor.isNativePlatform();
  const listenersSetupRef = useRef(false);
  const tokenRef = useRef<string | null>(null);

  // Check if we've already prompted for permission
  const hasPromptedPermission = useCallback(() => {
    return localStorage.getItem(PERMISSION_PROMPTED_KEY) === 'true';
  }, []);

  // Mark that we've prompted for permission
  const markPermissionPrompted = useCallback(() => {
    localStorage.setItem(PERMISSION_PROMPTED_KEY, 'true');
  }, []);

  // Register device token with backend
  const registerToken = useCallback(async (token: string) => {
    if (!user) {
      return;
    }
    tokenRef.current = token;

    const platform = Capacitor.getPlatform() as 'ios' | 'android';

    const logFailure = async (stage: string, err: unknown) => {
      const message =
        err && typeof err === 'object' && 'message' in err
          ? String((err as { message: unknown }).message)
          : String(err);
      const code =
        err && typeof err === 'object' && 'code' in err
          ? String((err as { code: unknown }).code)
          : null;
      console.error(`[Push] ${stage} failed:`, err);
      try {
        await supabase.from('analytics_events').insert({
          user_id: user.id,
          event_name: 'push_register_failed',
          event_data: { stage, message, code, platform },
        });
      } catch {
        // best-effort analytics
      }
    };

    try {
      // Guarantee a valid session before writing (avoids silent 401s on stale token)
      await ensureSession();

      // Check if device with this token already exists
      const { data: existingDevice, error: lookupError } = await supabase
        .from('devices')
        .select('id')
        .eq('user_id', user.id)
        .eq('push_token', token)
        .maybeSingle();

      if (lookupError) {
        await logFailure('lookup', lookupError);
        return;
      }

      if (existingDevice) {
        const { error } = await supabase
          .from('devices')
          .update({
            user_id: user.id,
            platform,
            device_name: `${platform.toUpperCase()} Device`,
            is_active: true,
            last_seen_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          })
          .eq('id', existingDevice.id);

        if (error) {
          await logFailure('update', error);
          return;
        }
      } else {
        const { error } = await supabase
          .from('devices')
          .insert({
            user_id: user.id,
            platform,
            push_token: token,
            device_name: `${platform.toUpperCase()} Device`,
            is_active: true,
            last_seen_at: new Date().toISOString(),
          })
          .select('id')
          .single();

        if (error) {
          await logFailure('insert', error);
          return;
        }
      }

      // Client-side funnel event so PostHog holds the full chain:
      // shown -> accepted -> os_prompt_result -> token_registered.
      try {
        const ph = (window as unknown as { posthog?: { capture: (e: string, p?: Record<string, unknown>) => void } }).posthog;
        ph?.capture('push_token_registered', { platform });
      } catch {
        /* non-fatal */
      }

      // Success breadcrumb (one per session is fine — register fires on each launch)
      supabase.from('analytics_events').insert({
        user_id: user.id,
        event_name: 'push_register_success',
        event_data: { platform },
      }).then(() => {});
    } catch (err) {
      await logFailure('exception', err);
    }
  }, [user]);

  // Handle incoming notification while app is in foreground
  // Note: We don't show in-app toast - iOS will still display the native notification
  const handleNotificationReceived = useCallback((notification: PushNotificationSchema) => {
    // Native iOS notifications will still appear in Notification Center even when app is foregrounded
  }, []);

  // Handle notification tap (app opened from notification)
  const handleNotificationAction = useCallback((action: ActionPerformed) => {

    // Clear badge when app is opened from notification
    if (isNative) {
      PushNotifications.removeAllDeliveredNotifications();
    }

    // Handle deep linking based on notification data
    const data = action.notification.data;

    // Comeback push re-engagement analytics — fire BEFORE navigation so the
    // event lands while the page is still mounted.
    const kind = (data?.kind || data?.trigger_type) as string | undefined;
    if (kind === 'comeback_day5' || kind === 'comeback_day10') {
      try {
        // posthog-js is initialized globally on window in main.tsx
        const posthog = (window as unknown as { posthog?: { capture: (e: string, p?: Record<string, unknown>) => void } }).posthog;
        posthog?.capture('comeback_push_reopened', { kind });
      } catch {
        /* non-fatal */
      }
      // Also log to server-side analytics for parity with sent events.
      if (user) {
        supabase.from('analytics_events').insert({
          user_id: user.id,
          event_name: 'comeback_push_reopened',
          event_data: { kind },
        }).then(() => {});
      }
    }

    if (kind === 'weekly_recap_sunday') {
      try {
        const posthog = (window as unknown as { posthog?: { capture: (e: string, p?: Record<string, unknown>) => void } }).posthog;
        posthog?.capture('weekly_recap_sunday_opened');
      } catch {
        /* non-fatal */
      }
      if (user) {
        supabase.from('analytics_events').insert({
          user_id: user.id,
          event_name: 'weekly_recap_sunday_opened',
          event_data: { kind },
        }).then(() => {});
      }
    }

    if (data?.route) {
      // Navigate to specific route if provided
      window.location.href = data.route;
    }
  }, [isNative, user]);

  // Check current permission status
  const checkPermissionStatus = useCallback(async () => {
    if (!isNative) return 'denied';
    
    try {
      const status = await PushNotifications.checkPermissions();
      return status.receive;
    } catch {
      return 'denied';
    }
  }, [isNative]);

  // Request permission and register for push notifications
  // Called only after meaningful user action
  const requestPermission = useCallback(async () => {
    if (!isNative) return false;

    try {
      // Check current permission status
      const permStatus = await PushNotifications.checkPermissions();
      
      if (permStatus.receive === 'prompt') {
        // Request permission
        const result = await PushNotifications.requestPermissions();
        markPermissionPrompted();
        
        if (result.receive !== 'granted') {
          return false;
        }
      } else if (permStatus.receive !== 'granted') {
        return false;
      }

      // Register with APNs/FCM
      await PushNotifications.register();
      return true;
    } catch (err) {
      console.error('Error requesting push permission:', err);
      return false;
    }
  }, [isNative, markPermissionPrompted]);

  // Request permission after a meaningful action
  // This is the recommended way to request permission
  const requestPermissionAfterAction = useCallback(async (trigger: NotificationTrigger) => {
    if (!isNative || hasPromptedPermission()) return false;
    
    // Only request on meaningful triggers
    const meaningfulTriggers: NotificationTrigger[] = [
      'workout_completed',
      'routine_saved', 
      'streak_started',
      'onboarding_complete',
    ];
    
    if (!meaningfulTriggers.includes(trigger)) return false;
    
    return requestPermission();
  }, [isNative, hasPromptedPermission, requestPermission]);

  // Set up listeners once per session (guarded at module scope so multiple
  // mount sites — AppLayout, Settings, Onboarding, ActiveWorkout — don't
  // each register their own duplicate handlers).
  useEffect(() => {
    if (!isNative || !user || globalListenersSetup) return;
    globalListenersSetup = true;
    listenersSetupRef.current = true;

    PushNotifications.addListener('registration', (token: Token) => {
      registerToken(token.value);
    });

    PushNotifications.addListener('registrationError', (error) => {
      console.error('Push registration error:', error);
    });

    PushNotifications.addListener(
      'pushNotificationReceived',
      handleNotificationReceived
    );

    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      handleNotificationAction
    );

    // Check if already granted and register silently
    (async () => {
      const status = await checkPermissionStatus();
      if (status === 'granted') {
        await PushNotifications.register();
      }
    })();

    // No cleanup: listeners must persist for the full session.
  }, [isNative, user, registerToken, handleNotificationReceived, handleNotificationAction, checkPermissionStatus]);

  // Update last seen timestamp periodically
  useEffect(() => {
    if (!isNative || !user) return;

    const updateLastSeen = async () => {
      try {
        await ensureSession();
        const platform = Capacitor.getPlatform() as 'ios' | 'android' | 'web';
        await supabase
          .from('devices')
          .update({ last_seen_at: new Date().toISOString() })
          .eq('user_id', user.id)
          .eq('platform', platform);
      } catch {
        // ignore - next interval will retry
      }
    };

    // Update on mount and every 5 minutes
    updateLastSeen();
    const interval = setInterval(updateLastSeen, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, [isNative, user]);

  // Send notification to the current user.
  // Returns { ok, data, error } so callers (e.g. the test button) can show full diagnostics.
  const sendNotification = useCallback(async (
    title: string,
    body: string,
    notificationType: string,
    data?: Record<string, unknown>
  ): Promise<{ ok: boolean; data: unknown; error: unknown }> => {
    if (!user) return { ok: false, data: null, error: 'No authenticated user' };
    // No-op on web — push notifications are native-only, avoids wasted round-trip.
    if (!Capacitor.isNativePlatform()) return { ok: true, data: null, error: null };



    try {
      const { data: response, error } = await supabase.functions.invoke('send-push-notification', {
        body: {
          user_id: user.id,
          title,
          body,
          notification_type: notificationType,
          data,
        },
      });

      if (error) {
        console.error('[Push] send-push-notification error:', error, 'response:', response);
        return { ok: false, data: response ?? null, error };
      }

      return { ok: true, data: response, error: null };
    } catch (err) {
      console.error('[Push] Failed to invoke send-push-notification:', err);
      return { ok: false, data: null, error: err };
    }
  }, [user]);

  // Send a test notification to the current user
  const sendTestNotification = useCallback(async () => {
    return sendNotification(
      '🎉 Push Notifications Working!',
      'Your device is now set up to receive notifications from My Pocket Coach.',
      'welcome'
    );
  }, [sendNotification]);

  // Trigger-specific notification helpers
  const notifyWorkoutComplete = useCallback(async (workoutName: string) => {
    // Also try to request permission on first workout
    await requestPermissionAfterAction('workout_completed');
    
    return sendNotification(
      '💪 Workout Complete!',
      `Great job finishing ${workoutName}! Keep the momentum going.`,
      'goal_achieved',
      { route: '/progress' }
    );
  }, [sendNotification, requestPermissionAfterAction]);

  const notifyStreakWarning = useCallback(async (streakDays: number) => {
    return sendNotification(
      '🔥 Don\'t Break Your Streak!',
      `You're on a ${streakDays}-day streak! Log your workout today to keep it alive.`,
      'streak_reminder',
      { route: '/workout' }
    );
  }, [sendNotification]);

  const notifyCoachFollowup = useCallback(async (message: string) => {
    return sendNotification(
      '💬 Message from your Coach',
      message,
      'coach_message',
      { route: '/coach' }
    );
  }, [sendNotification]);

  return {
    isNative,
    hasPromptedPermission,
    checkPermissionStatus,
    requestPermission,
    requestPermissionAfterAction,
    sendTestNotification,
    sendNotification,
    notifyWorkoutComplete,
    notifyStreakWarning,
    notifyCoachFollowup,
  };
}
